import { useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";
import Slider from "react-slick";
import weekdayEnum from "../../../../constants/enums/weekday-enum";
import { useStore } from "../../../../contexts/store-provider";
import {
  IStaffCMSVM,
  ITreatmentWithOptionVM,
} from "../../../../models/response/IStoreCmsResponse";
import { formatMessage } from "../../../../translations/format-message";
import StoreServiceListSection from "./store-services-list-section";
import StoreStaffListSection from "./store-staff-list-section";
import { StoreCmsStore } from "../../../../core/stores/store-cms-store";
import { observer } from "mobx-react";
import RoutesConstants from "../../../../constants/routes.constant";

interface IStore {
  limit: number;
  storeName: string;
  desc: string;
  address: string;
  prefectureName: string;
  images: string[];
  mobileNumber: string;
  accessInformation: string;
  storeMapURL: string;
  storeDetails: any; // Assuming storeDetails is not used in this component
  staffList: IStaffCMSVM[];
  workingHours: any;
  treatmentList: ITreatmentWithOptionVM[];
  setKey: (key: string) => void;
}

const StoreIntroductionTab = (props: IStore) => {
  const history = useHistory();
  const { bookingManagementStore, storeCmsStore } = useStore();
  const { setIsSourceFeatured, setSelectedService } = bookingManagementStore;
  const { storeCodeDetails, resetCMSTreatmentList } = storeCmsStore;
  const slider1 = useRef<Slider>(null);
  const slider2 = useRef<Slider>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const mainSettings = {
    arrows: false,
    fade: true,
    infinite: true,
    beforeChange: (_: number, next: number) => setActiveIndex(next), // track active
  };

  const thumbSettings = {
    slidesToShow: Math.min(props.images.length, 4),
    swipeToSlide: true,
    focusOnSelect: false, // turn this off, we’ll handle clicks manually
    infinite: false,
    arrows: true,
  };

  const handleThumbClick = (index: number) => {
    setActiveIndex(index);
    slider1.current?.slickGoTo(index); // move main slider
    slider2.current?.slickGoTo(index); // keep thumbs aligned
  };
  
  const handleBooking = () => {
    // Handle booking logic here
    // new StoreCmsStore().resetStoreCodeDetails();
    setSelectedService(undefined);
    setIsSourceFeatured(false);
    history.push(`/store/${storeCodeDetails.StoreCode}/reservation`);
  };

  useEffect(()=>{
    return(()=>{
      resetCMSTreatmentList();
    })
  },[])

  return (
    <>
      <h1 className="fw-normal">
        <Button 
          variant="light" 
          className="d-inline-block d-xl-none me-3"
          onClick={() => history.push(RoutesConstants.StoreListPage)}
        >
          <i className="bi bi-arrow-left text-primary"></i>
        </Button>
        {props.storeName}
      </h1>
      <div className="store-info-header text-wrap mb-3">
          <p className="store-address d-flex align-items-center gap-2">
            <i className="bi bi-geo-alt-fill text-primary fs-4"></i>{" "}
            {props.prefectureName} {props.address}
          </p>
          <p className="store-phone-number d-flex align-items-center gap-2">
            <i className="bi bi-telephone-fill fs-4"></i> {props.mobileNumber}
          </p>
        <p className="store-description">{props.accessInformation}</p>
      </div>
      <Row>
        <Col md={12} lg={6}>
          {props.images?.length == 1 ? (
            <img
              src={props.images[0]}
              alt="Store"
              className="img-fluid w-100"
            />
          ) : (
            <Row>
              <Col xs={12} className="mb-3">
        <Slider {...mainSettings} ref={slider1}>
          {props.images.map((img, idx) => (
            <div key={idx}>
              <img
                src={img}
                alt={`Slide ${idx + 1}`}
                className="img-fluid w-100 main-slider-img"
              />
            </div>
          ))}
        </Slider>
      </Col>

      <Col xs={12}>
        <Slider {...thumbSettings} ref={slider2}>
          {props.images.map((img, idx) => (
            <div
              key={idx}
              className={`px-1 ${idx === activeIndex ? "border border-primary" : ""}`}
              onClick={() => handleThumbClick(idx)}
              style={{ cursor: "pointer" }}
            >
              <img
                src={img}
                alt={`Thumbnail ${idx + 1}`}
                className="img-fluid thumbnail-img"
              />
            </div>
          ))}
        </Slider>
      </Col>
            </Row>
          )}
        </Col>
        <Col md={12} lg={6}>
          <div className="text-wrap">
            <div
              dangerouslySetInnerHTML={{
                __html:
                  props.storeDetails.StoreDetails ,
              }}
            ></div>
          </div>
          <div className="text-end store_detail_action_buttons">
            <Button
              variant="info"
              onClick={handleBooking}
              className="btn-lg w-100 w-xl-auto mt-xl-4"
            >
              <FormattedMessage id="book_now" />
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs={12} sm={12} md={12} lg={8} xl={9} xxl={9}>
          {/* store's featured treatment list */}
          <h2 className="fw-normal mt-4">
            <FormattedMessage id="featured_services" />
          </h2>
          {props.treatmentList && props.treatmentList.length > 0 && (
            <Row>
              {props.treatmentList.map((treatment, index) => (
                <StoreServiceListSection
                  key={index}
                  treatment={treatment}
                  store={props.storeDetails}
                  storeCode={storeCodeDetails.StoreCode}
                />
              ))}
            </Row>
          ) }
          <Button
            variant="primary"
            onClick={() => props.setKey("reviews")}
            className="mt-3"
          >
            <FormattedMessage id="show_more" />
          </Button>

          {/* store's staff list */}
          <h2 className="fw-normal mt-4">
            <FormattedMessage id="Staff" />
          </h2>
          {props.staffList && props.staffList.length > 0 ? (
            <Row className="g-3">
              {props.staffList.map(
                (staff, index) =>
                  index < props.limit && (
                    <StoreStaffListSection
                      key={index}
                      staff={staff}
                      storeDetails={props.storeDetails}
                    />
                  )
              )}
            </Row>
          ) : (
            <p className="text-muted">
              <FormattedMessage id="no_staff_available" />
            </p>
          )}
            <Button
              variant="primary"
              onClick={() => props.setKey("staff")}
              className="mt-3"
            >
              <FormattedMessage id="show_more" />
            </Button>

            <Button 
              variant="danger" 
              className="mt-xl-3 ms-3 d-xl-inline-block d-none "
              onClick={() => history.push(RoutesConstants.StoreListPage)}
            >
              <i className="bi bi-arrow-left"></i> &nbsp;
              <FormattedMessage id="back_to_store_list" />
            </Button>
        </Col>

        <Col xs={12} sm={12} md={12} lg={4} xl={3} xxl={3}>
          {/* Store's working hours */}
          <div className="border my-3">
            <h3 className="p-3 border-bottom fw-normal">
              <FormattedMessage id="working_hours" />
            </h3>
            <div className="p-3">
              {props.workingHours.IsVisible === "0" ? (
                <>
                  {props.workingHours?.BusinessSettings?.map((hours: any) => {
                    return (
                      <Row>
                        <Col xs={5} lg={5}>
                          {hours.WeekDays == 7 && hours.IsVisible == true
                            ? formatMessage("public_holiday")
                            : hours.WeekDays == 8
                            ? formatMessage("holidays")
                            : formatMessage(weekdayEnum[hours.WeekDays]?.toLowerCase())}
                        </Col>
                        <Col xs={7} lg={7}>
                          {hours.IsVisible == true
                            ? hours.IsClosed == true
                              ? formatMessage("closed")
                              : `${hours.OpenTime} ~ ${hours.ClosedTime}`
                            : ""}
                        </Col>
                      </Row>
                    );
                  })}
                  {props.workingHours?.IsHolidayVisible && (
                    <Row>
                      <Col xs={12} lg={12} xl={12} xxl={12}>
                        <p className="text-wrap">{props.workingHours?.HolidaysMessage}</p>
                      </Col>
                    </Row>
                  )}
                </>
                ) : (
                <p className="text-muted">
                  <FormattedMessage id="irregular_working_hours" />
                </p>
              )}              
            </div>
          </div>

          {/* Store's access Information */}
          <div className="border my-3">
            <h3 className="p-3 border-bottom fw-normal">
              <FormattedMessage id="place" />
            </h3>
            <div className="p-3 place_frame_wrap">
              <div dangerouslySetInnerHTML={{ __html: props.storeMapURL }} />
            </div>
          </div>
          <p className="store-address text-wrap d-flex align-items-center gap-2">
            <i className="bi bi-geo-alt-fill text-primary fs-4"></i>{" "}
            {props.prefectureName}{props.address}
          </p>

          <p className="store-description text-wrap">
            {props.accessInformation}
          </p>
        </Col>
      </Row>
    </>
  );
};

export default observer(StoreIntroductionTab);
